<script lang="ts">
    import {CurrentMarker, TargetMarker} from "./SetupFormInputs/SVGMarkers/";
    import { round } from "@/Util/utils";

    export let label: string
    export let color: string
    export let lowAlphaColor: string = '#e6e6e6'
    export let colorRemaining: boolean = false
    export let xpmcDayTypeLabel: string = null
    export let forecastedDayTypeLabel: string
    export let showDiff: boolean = true

    export let min: number = 0
    export let value: number
    export let max: number
    export let ringWidth: number = 6
    export let precision: number = 0
    export let units: string = null

    type markerProperties = {label: string, color?: string}
    type InputMarker = markerProperties & {value: number}
    export let expectedMarker: InputMarker | null = null
    export let markers: InputMarker[] = []

    let markersPcnt: (markerProperties & {pcnt: number})[] = []
    let valuePcnt = 0
    let expectedValuePcnt = 0
    let realMin = min
    let realMax = max
    let valueColor = 'black'

    let diff = 0
    $: {
        const markerInput = expectedMarker ? [expectedMarker, ...markers] : markers
        // Ideally, the the correct min/max should be supplied by props, since this component can't see the date ranges on CurrentProgram.svelte
        value = round(value, precision)
        expectedMarker.value = round(expectedMarker.value, precision)
        realMin = round(Math.min(min, value, ...markerInput.map(({value}) => value)), precision)
        realMax = round(Math.max(max, value, ...markerInput.map(({value}) => value)), precision)
        const range = realMax - realMin

        valuePcnt = range ? ((value - realMin) / range) * 100 : 100
        markersPcnt = markerInput.map(({label, color, value}) => ({label, color, pcnt: range ? ((value - realMin) / range) * 100 : 100}))
        if (expectedMarker) {
            diff = value - expectedMarker.value
            expectedValuePcnt = range ? ((expectedMarker.value - realMin) / range) * 100 : 100
            valueColor = (round(diff, precision) >= 0) ? '#16a34a' : '#dc2626'
        } else {
            valueColor = 'black'
        }
        
    }
    
    const viewBoxSize = 120
    const circleCenter = (viewBoxSize / 2) - 2
    const radius = circleCenter - ringWidth
    const circumference = 2 * Math.PI * (radius)
    const limitedMeterOffset = circumference * (100 / 360)
    const innerMarkerRadius = ringWidth * 0.8
</script>

<div class="h-full aspect-square relative mx-auto min-w-[130px]"> <!-- min width 130px, below this the text looks too small -->
    <svg
        class="absolute left-0 top-0 w-full h-full"
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
    >
        <!-- Remaining gray arc -->
        <circle
            class="progress-ring__circle remaining"
            stroke={colorRemaining ? lowAlphaColor : '#e6e6e6'}
            stroke-width={ringWidth}
            stroke-dasharray={`${circumference} ${circumference}`}
            stroke-dashoffset={-1 * (limitedMeterOffset + ((circumference - limitedMeterOffset) * ((valuePcnt) / 100)))}
            stroke-linecap="round"
            fill="transparent"
            r={radius}
            cx={viewBoxSize / 2}
            cy={viewBoxSize / 2}
        />
        <!-- Expected Value -->
        <circle
            class="progress-ring__circle"
            stroke={color}
            stroke-width={ringWidth}
            stroke-dasharray={`${circumference} ${circumference}`}
            stroke-dashoffset={limitedMeterOffset + ((circumference - limitedMeterOffset) * (1 - (expectedValuePcnt / 100)))}
            stroke-linecap="round"
            fill="transparent"
            r={radius}
            cx={viewBoxSize / 2}
            cy={viewBoxSize / 2}
            opacity=0.3
        />
        <!-- Current Value -->
        <circle
            class="progress-ring__circle"
            stroke={color}
            stroke-width={ringWidth}
            stroke-dasharray={`${circumference} ${circumference}`}
            stroke-dashoffset={limitedMeterOffset + ((circumference - limitedMeterOffset) * (1 - (valuePcnt / 100)))}
            stroke-linecap="round"
            fill="transparent"
            r={radius}
            cx={viewBoxSize / 2}
            cy={viewBoxSize / 2}
        />

        <!-- Min/Max meter text on left and right ends -->
        <text x={12} y={110} font-size="11px" class="svg-text-middle">
            {realMin.toFixed(precision)}
        </text>
        <text x={108} y={110} font-size="11px" class="svg-text-middle">
            {realMax.toFixed(precision)}
        </text>

        <!-- Center big text -->
        {#if !expectedMarker}
            <text class="svg-text-middle" x={viewBoxSize / 2} y={viewBoxSize / 2} font-size="42px" font-weight="bold">
                {value.toFixed(precision)}
            </text>
        {:else}
            {@const yShiftUp = xpmcDayTypeLabel ? 5 : 8}
            <text class="svg-text-middle" x={viewBoxSize / 2} y={(viewBoxSize / 2) - yShiftUp} font-size="0px">
                <tspan class="svg-text-middle" font-size="36px" font-weight="bold">
                    {value.toFixed(precision)}
                </tspan>
                <tspan x={viewBoxSize / 2} dy={27} font-size="10px" font-weight="normal" fill={showDiff ? valueColor : '#475569'}>
                    {#if showDiff}
                        {round(Math.abs(diff), precision)}{` `}
                        {#if round(diff, precision) >= 0}
                            Ahead
                        {:else}
                            Behind
                        {/if}
                    {:else}
                        {round(expectedMarker.value, precision)}{` `}{forecastedDayTypeLabel}
                    {/if}
                </tspan>
                {#if xpmcDayTypeLabel}
                    <tspan x={viewBoxSize / 2} dy={-50} font-size="10px" font-weight="normal" fill='#6b7280'>
                        {xpmcDayTypeLabel}
                    </tspan>
                {/if}
            </text>
        {/if}
        <!-- <g class="flex flex-col gap-4">
            <text class="text-2xl font-bold" x={circleCenter -15} y={circleCenter - 12} dominant-baseline="middle">
                {expectedMarker.value.toFixed(precision)}
            </text>
            <TargetMarker class="text-blue-400" r={innerMarkerRadius*1.3} x={circleCenter - 25} y={circleCenter - 12} />
            <text class="text-2xl" x={circleCenter -15} y={circleCenter + 12} dominant-baseline="middle">
                {value.toFixed(precision)}
            </text>
            <CurrentMarker class="text-blue-400" r={innerMarkerRadius} x={circleCenter - 25} y={circleCenter + 8} />
        </g> -->

        <!-- Rounded box background for label -->
        <!-- <rect 
            x ="32" y ="98" 
            height ="18" width ="56"
            rx ="4" ry ="4"
            fill={lowAlphaColor}
            stroke={lowAlphaColor}
        /> -->
        <!-- Label -->
        <text x="60" y="108" font-size="12px" fill={color} font-weight='bold' class="svg-text-middle">
            {label}
            {#if units}
                {` (${units})`}
            {/if}
        </text>
        
        <!-- Markers -->
        {#each markersPcnt as marker}
            {@const currentDeg = ((valuePcnt / 100) * 260) + 50}
            {@const targetDeg = ((marker.pcnt / 100 * 260) + 50)}

            <g
                class="origin-center marker-graph"
                style="transform: rotate({targetDeg}deg)"  
            >   
                <TargetMarker class="text-blue-400" r={innerMarkerRadius*1.5} y={viewBoxSize/2 - ringWidth - 2}/>
            </g>

            <g
                class="origin-center marker-graph"
                style="transform: rotate({currentDeg}deg)"  
            >
                <CurrentMarker class="text-blue-400" r={innerMarkerRadius} y={viewBoxSize/2 - ringWidth - 2}/>
            </g>

            <!-- <g
                class="origin-center marker-graph"
                transform="rotate({currentDeg})"  
            >   
                <DottedLineMarker height="20" y={viewBoxSize + ringWidth + 2}/>
            </g> -->
            
        {/each}

        
        
    </svg>
</div>

<style>
    .progress-ring__circle {
        transition: 0.35s stroke-dashoffset linear;
        transform-origin: 50% 50%;
        transform: rotate(140deg);
        &.remaining {
            transform: rotate(40deg);
        }
    }
    .svg-text-middle {
        /* vscode says this property doesn't exist, but its an SVG property and does get applied correctly by the browser */
        dominant-baseline: middle;
        text-anchor: middle;
    }
    .marker-graph {
        transition: 0.35s transform linear;
    }
</style>